import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import partnerApi from '../../api/partnerApi';
import { Trophy, TrendingUp, Award, RefreshCw } from 'lucide-react';

const fmt = (n) => `\u20a6${Number(n || 0).toLocaleString()}`;

const TIER_COLORS = {
  silver:   { bg: 'bg-slate-100', text: 'text-slate-600' },
  gold:     { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  platinum: { bg: 'bg-blue-100', text: 'text-blue-700' },
  diamond:  { bg: 'bg-purple-100', text: 'text-purple-700' },
};

const RANK_MEDALS = {
  1: { icon: '🥇', cls: 'bg-yellow-50 border-yellow-200 text-yellow-700 font-bold' },
  2: { icon: '🥈', cls: 'bg-slate-50 border-slate-200 text-slate-600 font-semibold' },
  3: { icon: '🥉', cls: 'bg-amber-50 border-amber-200 text-amber-700 font-semibold' },
};

export default function PartnerLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    partnerApi.get('/leaderboard')
      .then((res) => { if (res?.success) setData(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="partner-leaderboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Partner Leaderboard
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {data?.month ? `Top partners by commissions earned in ${data.month}` : 'This month\'s top performers'}
          </p>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          data-testid="leaderboard-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Current rank callout */}
      {data?.current_rank && (
        <div className="bg-[#EDF0FF] border border-[#C7D0FF] rounded-2xl p-4 flex items-center gap-4" data-testid="current-rank-card">
          <div className="w-12 h-12 rounded-full bg-[#516EFA] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
            #{data.current_rank}
          </div>
          <div>
            <p className="text-sm font-semibold text-[#3B4DBF]">Your Current Rank</p>
            <p className="text-xs text-[#516EFA] mt-0.5">
              {data.current_rank === 1
                ? 'You are #1 this month — outstanding!'
                : `Keep earning to move up the leaderboard`}
            </p>
          </div>
          <TrendingUp className="w-6 h-6 text-[#516EFA] ml-auto flex-shrink-0" />
        </div>
      )}

      {/* Leaderboard list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : !data?.leaderboard?.length ? (
        <div className="bg-white border border-slate-100 rounded-2xl p-12 text-center" data-testid="leaderboard-empty">
          <Trophy className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-medium text-sm">No commissions earned this month yet</p>
          <p className="text-xs text-slate-400 mt-1">Be the first to get on the board — share your referral link!</p>
          <button
            onClick={() => navigate('/reseller/dashboard')}
            className="mt-4 text-xs text-[#516EFA] hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="leaderboard-list">
          {data.leaderboard.map((entry) => {
            const medal = RANK_MEDALS[entry.rank];
            const tierColors = TIER_COLORS[entry.tier_name?.toLowerCase()] || TIER_COLORS.silver;
            return (
              <div
                key={entry.rank}
                className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                  entry.is_current
                    ? 'bg-[#EDF0FF] border-[#C7D0FF] shadow-sm ring-2 ring-[#516EFA]/20'
                    : medal
                    ? `${medal.cls} border`
                    : 'bg-white border-slate-100 hover:border-slate-200'
                }`}
                data-testid={`leaderboard-row-${entry.rank}`}
              >
                {/* Rank */}
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${
                  entry.is_current
                    ? 'bg-[#516EFA] text-white font-bold'
                    : medal
                    ? 'bg-white/70 font-bold text-slate-700'
                    : 'bg-slate-100 text-slate-500 font-semibold'
                }`}>
                  {medal ? medal.icon : `#${entry.rank}`}
                </div>

                {/* Name + code + tier */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-semibold ${entry.is_current ? 'text-[#3B4DBF]' : 'text-slate-800'}`}>
                      {entry.display_name}
                      {entry.is_current && <span className="ml-1 text-xs font-normal text-[#516EFA]">(You)</span>}
                    </span>
                    {entry.tier_name && (
                      <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${tierColors.bg} ${tierColors.text}`}>
                        <Award className="w-2.5 h-2.5" />
                        {entry.tier_name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 font-mono">{entry.partner_code_hint}</p>
                </div>

                {/* Commission */}
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm font-bold ${entry.is_current ? 'text-[#3B4DBF]' : entry.rank <= 3 ? 'text-slate-800' : 'text-slate-700'}`}>
                    {fmt(entry.monthly_commissions)}
                  </p>
                  <p className="text-xs text-slate-400">{entry.transaction_count} deal{entry.transaction_count !== 1 ? 's' : ''}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <p className="text-xs text-slate-400 text-center pb-2">
        Rankings are based on confirmed commissions earned in the current calendar month.
        Privacy: only first name and initial are shown.
      </p>
    </div>
  );
}
